import { useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { ConditionalFormatRule } from '../../../types/bi';
import styles from './QueryDesigner.module.css';

interface ConditionalFormatRulesProps {
  value: ConditionalFormatRule[];
  onChange: (rules: ConditionalFormatRule[]) => void;
}

const RULES: { value: ConditionalFormatRule['rule']; label: string }[] = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
  { value: 'between', label: 'between' },
];

const COLOURS = [
  { value: '#ef4444', label: 'Red' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#22c55e', label: 'Green' },
  { value: '#3b82f6', label: 'Blue' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#6b7280', label: 'Grey' },
];

export default function ConditionalFormatRules({ value, onChange }: ConditionalFormatRulesProps) {
  const add = useCallback(() => {
    onChange([...value, { rule: 'gt', threshold: 0, colour: '#ef4444' }]);
  }, [value, onChange]);

  const update = useCallback(
    (idx: number, patch: Partial<ConditionalFormatRule>) => {
      onChange(value.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    },
    [value, onChange],
  );

  const remove = useCallback(
    (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    },
    [value, onChange],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {value.map((r, i) => (
        <div key={i} className={styles.row} style={{ flexWrap: 'wrap', gap: '0.35rem' }}>
          <select
            className={styles.selectSm}
            value={r.rule}
            onChange={(e) => update(i, { rule: e.target.value as ConditionalFormatRule['rule'] })}
          >
            {RULES.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>

          <input
            type="number"
            className={styles.input}
            value={r.threshold}
            onChange={(e) => update(i, { threshold: Number(e.target.value) || 0 })}
            placeholder="Threshold"
            style={{ width: 80 }}
          />

          {r.rule === 'between' && (
            <input
              type="number"
              className={styles.input}
              value={r.threshold_upper ?? ''}
              onChange={(e) => update(i, { threshold_upper: Number(e.target.value) || 0 })}
              placeholder="Upper"
              style={{ width: 80 }}
            />
          )}

          <select
            className={styles.selectSm}
            value={r.colour}
            onChange={(e) => update(i, { colour: e.target.value })}
            style={{ minWidth: 80 }}
          >
            {COLOURS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>

          <button className={styles.removeBtn} onClick={() => remove(i)}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ))}

      <button className={styles.addBtn} onClick={add}>
        <FontAwesomeIcon icon={faPlus} /> Add rule
      </button>
    </div>
  );
}
